import { api, hasBackend } from "./api";

interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatar: string;
}

interface LoginResult {
  user: AuthUser;
  token: string;
}

const DEMO = { email: "demo@payflow.ai", password: "demo123" };

export async function login(email: string, password: string): Promise<LoginResult> {
  if (!hasBackend()) {
    if (email === DEMO.email && password === DEMO.password) {
      return { user: { id: "demo", name: "John Doe", email, avatar: "JD" }, token: "mock_token" };
    }
    throw new Error("Invalid credentials");
  }
  const res = await api.post<{ accessToken: string; user: Record<string, string> }>(
    "/api/v1/auth/login",
    { email, password }
  );
  return {
    token: res.accessToken,
    user: {
      id: res.user["id"] ?? "",
      name: res.user["name"] ?? "",
      email: res.user["email"] ?? email,
      avatar: (res.user["name"] ?? "")
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2),
    },
  };
}

export async function signup(name: string, email: string, password: string): Promise<LoginResult> {
  if (!hasBackend()) {
    if (password.length < 6) throw new Error("Password must be at least 6 characters");
    const avatar = name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    return { user: { id: String(Date.now()), name, email, avatar }, token: "mock_token" };
  }
  const res = await api.post<{ accessToken: string; user: Record<string, string> }>(
    "/api/v1/auth/signup",
    { name, email, password }
  );
  return {
    token: res.accessToken,
    user: {
      id: res.user["id"] ?? "",
      name,
      email,
      avatar: name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2),
    },
  };
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  if (!hasBackend()) return;
  await api.patch("/api/v1/auth/password", { currentPassword, newPassword });
}

export async function deleteAccount(): Promise<void> {
  if (!hasBackend()) return;
  await api.delete<void>("/api/v1/auth/me");
}

export async function forgotPassword(email: string): Promise<void> {
  if (!hasBackend()) return;
  await api.post("/api/v1/auth/forgot-password", { email });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  if (!hasBackend()) return;
  await api.post("/api/v1/auth/reset-password", { token, newPassword });
}
